import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Spinner from '../../../components/Spinner';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useAuthRequest } from '../../../services/apis/auth/useAuthRequest';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { saveLastLoginUser } from '../lastLoginUserStorage';
import { useAppStore } from '../../../stores/application/useAppStore';
import { parseStoredInviteValue, SSO_INVITE_CODE_SESSION_KEY } from '../../../interfaces/models/invite';

const getInviteCodeForSsoCallback = (searchParams: URLSearchParams): string | undefined => {
    const fromQuery = searchParams.get('invite')?.trim();
    if (fromQuery) return fromQuery;

    const fromSession = sessionStorage.getItem(SSO_INVITE_CODE_SESSION_KEY)?.trim();
    if (fromSession) return fromSession;

    return parseStoredInviteValue(localStorage.getItem('inviteValue'))?.inviteCode;
};

const clearSsoSession = () => {
    sessionStorage.removeItem('sso_email');
    sessionStorage.removeItem(SSO_INVITE_CODE_SESSION_KEY);
};

const getSsoErrorRedirect = (inviteCode?: string) =>
    inviteCode
        ? `${AppRoutes.SIGNUP}?invite=${encodeURIComponent(inviteCode)}`
        : AppRoutes.SIGNIN;

const SSOCallback: FunctionComponent = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { workOSCallback } = useAuthRequest();
    const { setAuthDataHandler } = useAuthHandlers();
    const { store: { toast } } = useAppStore();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Get the authorization code from URL
                const code = searchParams.get('code');
                const inviteCode = getInviteCodeForSsoCallback(searchParams);
                const error = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');
                
                const ssoEmail = sessionStorage.getItem('sso_email');
                const errorRedirect = getSsoErrorRedirect(inviteCode);

                if (error) {
                    console.error('WorkOS SSO error:', error, errorDescription);
                    setError(errorDescription || 'SSO authentication failed. Please try again.');
                    clearSsoSession();
                    
                    setTimeout(() => {
                        navigate(errorRedirect);
                    }, 3000);
                    return;
                }

                if (!code) {
                    console.error('No authorization code provided');
                    setError('Missing authorization code. Please try again.');
                    clearSsoSession();
                    
                    setTimeout(() => {
                        navigate(errorRedirect);
                    }, 3000);
                    return;
                }

                // Exchange code for tokens (include email if available)
                let response;
                try {
                    response = await workOSCallback(
                        { code, ...(ssoEmail && { email: ssoEmail }) }, 
                        inviteCode || undefined
                    );
                } catch (callbackError: any) {
                    // Extract error message from backend response
                    const errorMessage = callbackError?.response?.data?.message 
                        || callbackError?.message 
                        || 'Authentication failed. Please try again.';
                    
                    console.error('WorkOS callback error:', callbackError);
                    setError(errorMessage);
                    clearSsoSession();
                    
                    setTimeout(() => {
                        navigate(errorRedirect);
                    }, 3000);
                    return;
                }

                if (response?.user) {
                    clearSsoSession();
                    if (inviteCode) {
                        localStorage.removeItem('inviteValue');
                    }

                    saveLastLoginUser(response.user, 'sso');
                    
                    // Update auth data
                    await setAuthDataHandler({ 
                        accessToken: `Bearer ${response.token}`, 
                        refreshToken: response.refreshToken 
                    });

                    // Redirect to home
                    navigate(AppRoutes.HOME);
                } else {
                    // Use message from backend response or default message
                    const errorMessage = response?.message || 'Authentication failed. Please try again.';
                    setError(errorMessage);
                    clearSsoSession();
                    
                    setTimeout(() => {
                        navigate(errorRedirect);
                    }, 3000);
                }
            } catch (err: any) {
                console.error('SSO callback error:', err);
                
                const errorMessage = err?.response?.data?.message 
                    || err?.message 
                    || 'An error occurred during authentication.';
                const inviteCode = getInviteCodeForSsoCallback(searchParams);
                const errorRedirect = getSsoErrorRedirect(inviteCode);
                
                setError(errorMessage);
                clearSsoSession();
                
                setTimeout(() => {
                    navigate(errorRedirect);
                }, 3000);
            }
        };

        handleCallback();
    }, []);

    return (
        <Container>
            <Spinner showHideSpinner={true} />
            {error && (
                <ErrorMessage>
                    {error}
                    <br />
                    Redirecting to log in...
                </ErrorMessage>
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    width: 100%;
`;

const ErrorMessage = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #ef4444;
    font-size: 16px;
    line-height: 1.5;
    padding: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
`;

export default SSOCallback;

